import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { functions } from "../App";
import { useAuth } from "./AuthProvider";

export type QuizSelectableQuestionCounts = Record<
  string,
  { questionCount: number; standardCount: number }
>;

const quizCountsCache = new Map<string, QuizSelectableQuestionCounts>();
const quizCountsInflight = new Map<string, Promise<QuizSelectableQuestionCounts>>();

const getCacheKey = (quizIds?: string[], cacheBuster?: string | number | null) => {
  if (!quizIds?.length) {
    return `all:${cacheBuster ?? ""}`;
  }
  return `${[...quizIds].sort().join("|")}:${cacheBuster ?? ""}`;
};

export const fetchQuizSelectableQuestionCounts = async (
  quizIds?: string[],
  cacheBuster?: string | number | null
): Promise<QuizSelectableQuestionCounts> => {
  const cacheKey = getCacheKey(quizIds, cacheBuster);
  const cached = quizCountsCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const inflight = quizCountsInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const payload = quizIds?.length ? { quizIds } : {};
  const serverGetQuizSelectableQuestionCounts = httpsCallable(
    functions,
    "getQuizSelectableQuestionCounts"
  );
  const promise = serverGetQuizSelectableQuestionCounts(payload)
    .then((result) => (result.data || {}) as QuizSelectableQuestionCounts)
    .then((counts) => {
      quizCountsCache.set(cacheKey, counts);
      return counts;
    })
    .finally(() => {
      quizCountsInflight.delete(cacheKey);
    });

  quizCountsInflight.set(cacheKey, promise);
  return promise;
};

export const useQuizSelectableQuestionCounts = (
  quizIds?: string[],
  cacheBuster?: string | number | null
) => {
  const { isAdmin } = useAuth();
  const normalizedQuizIds = useMemo(
    () => (quizIds ?? []).filter((id): id is string => !!id),
    [quizIds]
  );
  const sortedQuizIds = useMemo(
    () => [...normalizedQuizIds].sort(),
    [normalizedQuizIds]
  );
  const cacheKey = useMemo(
    () => getCacheKey(sortedQuizIds, cacheBuster),
    [sortedQuizIds, cacheBuster]
  );
  const [counts, setCounts] = useState<QuizSelectableQuestionCounts>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!isAdmin) {
      setCounts({});
      quizCountsCache.clear();
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const cached = quizCountsCache.get(cacheKey);
    if (cached) {
      setCounts(cached);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    fetchQuizSelectableQuestionCounts(
      sortedQuizIds.length ? sortedQuizIds : undefined,
      cacheBuster
    )
      .then((data) => {
        if (!cancelled) {
          setCounts(data || {});
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setCounts({});
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin, cacheKey, sortedQuizIds, cacheBuster]);

  return { counts, loading, error };
};
